import { Component, OnInit } from '@angular/core';
import { DashboadService } from './dashboard.service';
import { Router } from '@angular/router';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexDataLabels,
  ApexPlotOptions,
  ApexTitleSubtitle,
  ApexLegend,
} from 'ng-apexcharts';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  title: ApexTitleSubtitle;
  legend: ApexLegend;
};

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
  dashboard: any;
  code =
    "Hi there! Let's secure your website with an SSL certificate. Could you share the domain name? \n\n ```\necho 'hello';\n``` ";
  public certificatesPieChart: Partial<ChartOptions> | any;
  public certificatesBarChart: Partial<ChartOptions> | any;
  public userPensPieChart: Partial<ChartOptions> | any;

  constructor(private service: DashboadService, private router: Router) {}
  ngOnInit() {
    this.fetchDashboard();
  }

  fetchDashboard() {
    this.service.getUserDashboard().subscribe({
      next: (response) => {
        this.dashboard = response;
        console.log(
          'dashboard.component.ts [4dabb7] fetchDashboard: ',
          response
        );
        this.createCertificatesPieChart();
        this.createCertificatesBarChart();
        this.createUserPensPieChart();
      },
      error: (error) => {
        console.error(
          'dashboard.component.ts [33fb16] fetchDashboard error: ',
          error
        );
      },
    });
  }
  createCertificatesPieChart() {
    this.certificatesPieChart = {
      series: [
        this.dashboard.certificates.active,
        this.dashboard.certificates.noCerts,
        this.dashboard.certificates.expired,
      ],
      chart: {
        type: 'donut',
        height: 350,
        background: 'transparent',
        fontFamily: 'Inter, sans-serif',
        foreColor: '#fff',
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
        dropShadow: {
          enabled: true,
          top: 0,
          left: 0,
          blur: 3,
          opacity: 0.2,
        },
      },
      colors: ['#3b82f6', '#ef4444', '#f59e0b'], // Blue for active, Red for no certs, Yellow for expired
      plotOptions: {
        pie: {
          donut: {
            size: '55%',
            background: 'transparent',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '22px',
                fontFamily: 'Inter, sans-serif',
                color: '#fff',
                offsetY: -10,
              },
              value: {
                show: true,
                fontSize: '26px',
                fontFamily: 'Inter, sans-serif',
                color: '#fff',
                offsetY: 16,
                formatter: function (val: any) {
                  return val;
                },
              },
              total: {
                show: true,
                showAlways: true,
                label: 'Total',
                fontSize: '16px',
                fontFamily: 'Inter, sans-serif',
                color: '#fff',
                formatter: function (w: any) {
                  return w.globals.seriesTotals.reduce(
                    (a: number, b: number) => a + b,
                    0
                  );
                },
              },
            },
          },
        },
      },
      labels: ['Active', 'No Certificates', 'Expired'],
      title: {
        text: '',
        align: 'center',
        style: {
          fontSize: '20px',
          fontWeight: 'bold',
          fontFamily: 'Inter, sans-serif',
          color: '#fff',
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        width: 0,
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '14px',
        fontFamily: 'Inter, sans-serif',
        labels: {
          colors: '#fff',
          useSeriesColors: false,
        },
        markers: {
          width: 12,
          height: 12,
          radius: 6,
        },
        itemMargin: {
          horizontal: 15,
          vertical: 5,
        },
        formatter: function (seriesName: string, opts: any) {
          return ['  ' + seriesName + '  '];
        },
      },
      tooltip: {
        enabled: true,
        theme: 'dark',
        style: {
          fontSize: '14px',
          fontFamily: 'Inter, sans-serif',
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              height: 300,
            },
            legend: {
              position: 'bottom',
            },
          },
        },
      ],
    };
  }

  createCertificatesBarChart() {
    this.certificatesBarChart = {
      series: [
        {
          name: 'Certificates',
          data: [
            this.dashboard.certificates.installedThisMonth,
            this.dashboard.certificates.installedLastMonth,
            this.dashboard.certificates.totalLastMonth,
          ],
        },
      ],
      chart: {
        type: 'area',
        height: 350,
        background: 'transparent',
        fontFamily: 'Inter, sans-serif',
        foreColor: '#fff',
        toolbar: {
          show: false,
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
        },
        dropShadow: {
          enabled: true,
          top: 0,
          left: 0,
          blur: 3,
          opacity: 0.2,
        },
      },
      colors: ['#3b82f6'], // Blue to match the certificate theme
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'dark',
          type: 'vertical',
          shadeIntensity: 0.3,
          gradientToColors: undefined,
          inverseColors: false,
          opacityFrom: 0.8,
          opacityTo: 0.2,
          stops: [0, 100],
        },
      },
      stroke: {
        width: 3,
        curve: 'smooth',
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        categories: [
          'Installed This Month',
          'Installed Last Month',
          'Total Last Month',
        ],
        labels: {
          style: {
            colors: '#fff',
            fontSize: '12px',
            fontFamily: 'Inter, sans-serif',
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: '#fff',
            fontSize: '12px',
            fontFamily: 'Inter, sans-serif',
          },
        },
      },
      title: {
        text: '',
        align: 'center',
        style: {
          fontSize: '20px',
          fontWeight: 'bold',
          fontFamily: 'Inter, sans-serif',
          color: '#fff',
        },
      },
      grid: {
        borderColor: 'rgba(255,255,255,0.1)',
        strokeDashArray: 5,
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      tooltip: {
        theme: 'dark',
        style: {
          fontSize: '14px',
          fontFamily: 'Inter, sans-serif',
        },
        y: {
          formatter: function (val: number) {
            return val.toString();
          },
        },
      },
      markers: {
        size: 5,
        colors: ['#3b82f6'],
        strokeColors: '#fff',
        strokeWidth: 2,
        hover: {
          size: 7,
        },
      },
    };
  }

  createUserPensPieChart() {
    this.userPensPieChart = {
      series: [
        {
          name: 'Pen Tests',
          data: [
            this.dashboard.userPens.total,
            this.dashboard.userPens.installedThisMonth,
          ],
        },
      ],
      chart: {
        type: 'line',
        height: 350,
        background: 'transparent',
        fontFamily: 'Inter, sans-serif',
        foreColor: '#fff',
        toolbar: {
          show: false,
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
        },
        dropShadow: {
          enabled: true,
          top: 0,
          left: 0,
          blur: 3,
          opacity: 0.2,
        },
      },
      colors: ['#a855f7'], // Purple to match pen test theme
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'dark',
          type: 'horizontal',
          shadeIntensity: 0.5,
          gradientToColors: ['#d8b4fe'],
          inverseColors: false,
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 100],
        },
      },
      stroke: {
        width: 6,
        curve: 'smooth',
      },
      markers: {
        size: 8,
        colors: ['#a855f7'],
        strokeColors: '#fff',
        strokeWidth: 2,
        hover: {
          size: 10,
        },
      },
      xaxis: {
        categories: ['Total Domains', 'Added This Month'],
        labels: {
          style: {
            colors: '#fff',
            fontSize: '12px',
            fontFamily: 'Inter, sans-serif',
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: '#fff',
            fontSize: '12px',
            fontFamily: 'Inter, sans-serif',
          },
          formatter: function (val: number) {
            return val.toFixed(0);
          },
        },
      },
      dataLabels: {
        enabled: true,
        background: {
          enabled: true,
          foreColor: '#000',
          padding: 4,
          borderRadius: 4,
          borderWidth: 0,
          opacity: 0.7,
        },
        style: {
          fontSize: '14px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 'bold',
          colors: ['#fff'],
        },
        offsetY: -10,
      },
      tooltip: {
        theme: 'dark',
        style: {
          fontSize: '14px',
          fontFamily: 'Inter, sans-serif',
        },
        y: {
          formatter: function (val: number) {
            return val.toString();
          },
        },
      },
      grid: {
        borderColor: 'rgba(255,255,255,0.1)',
        strokeDashArray: 5,
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      annotations: {
        points: [
          {
            x: 'Total Domains',
            y: this.dashboard.userPens.total,
            marker: {
              size: 0,
            },
            label: {
              borderColor: '#a855f7',
              offsetY: 0,
              style: {
                color: '#fff',
                background: '#a855f7',
              },
              text: 'Total',
            },
          },
          {
            x: 'Added This Month',
            y: this.dashboard.userPens.installedThisMonth,
            marker: {
              size: 0,
            },
            label: {
              borderColor: '#a855f7',
              offsetY: 0,
              style: {
                color: '#fff',
                background: '#a855f7',
              },
              text: 'Added',
            },
          },
        ],
      },
    };
  }

  goTo(link: string) {
    this.router.navigate([link]);
  }
}
